import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { FC, useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import __ from "web/Layout/Translations";

import Dialog from "web/Components/mui/Dialog";

import prepareSubscriptionGroupsSummaryForCurrentUser from "web/utils/page/customer/prepareSubscriptionGroupsSummaryForCurrentUser";
import isHiddenAutomaticBenefit from "web/utils/page/product/subscription/isHiddenAutomaticBenefit";

import {
  FormType,
  HandleToggleReceiversModalFn,
  ISubscriptionItemCurrentUserSubscription,
  ISubscriptionTotals,
  ISubscriptionUserCurrent,
} from "web/types/Subscription";

import { useAppContext } from "web/context/app";
import { subscriptionsApiSlice } from "web/features/subscriptions/subscriptionsApiSlice";

import ActivityItemMUI, { ActivityItemProps } from "../activityItemMUI";
import ItemFormToFillMUI from "./ItemFormToFill/itemFormToFillMUI";

interface IFormFillerActivityItemItemProps extends ActivityItemProps {
  benefitsWithFormsToUpdate: ISubscriptionItemCurrentUserSubscription[];
  setDataRequiredActivities: React.Dispatch<
    React.SetStateAction<ISubscriptionUserCurrent>
  >;
  totals: ISubscriptionTotals;
}

type UpdatedReceiverType = Parameters<HandleToggleReceiversModalFn>[0];

const FormFillerItem: FC<IFormFillerActivityItemItemProps> = ({
  benefitsWithFormsToUpdate,
  setDataRequiredActivities,
  totals,
  ...restProps
}) => {
  const { isMobile } = useAppContext();
  const dispatchRedux = useDispatch();

  const [editDataDialogOpen, setEditDataDialogOpen] = useState(false);
  const [itemsToUpdate, setItemsToUpdate] = useState<
    ISubscriptionItemCurrentUserSubscription[]
  >([]);

  const description = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        minWidth: "283px",
        bgcolor: "#FFF4E5",
        py: { xs: 1, md: 2 },
        px: 2,
        mb: 2,
      }}
    >
      <WarningAmberIcon
        color="warning"
        sx={{
          width: "16px",
          mr: "12px",
          position: "relative",
          top: "-5px",
        }}
      />
      <Typography variant="body2" sx={{ color: "#663C00", width: "100%" }}>
        {__(
          "Uzupełnij swoje dane dotyczące abonamentów, które wybrałeś oraz wpisz adres e-mail osób, dla których wybrałeś abonament."
        )}
      </Typography>
    </Box>
  );

  const onCloseDialogHandler = () => {
    setEditDataDialogOpen(false);
    setItemsToUpdate([]);
    dispatchRedux(
      subscriptionsApiSlice.util.invalidateTags(["CurrentUserSubscriptions"])
    );
  };

  const onOpenDialogHandler = useCallback(
    (updatedReceiver?: UpdatedReceiverType) => {
      let updatedItems: ISubscriptionItemCurrentUserSubscription[] = [];

      if (updatedReceiver) {
        setDataRequiredActivities((prevData) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          updatedItems = prevData.items.map((item) => {
            const receivers = item.receivers?.map((receiver) => {
              if (receiver.id === updatedReceiver.id) {
                const updatedFields = receiver.form.fields.map((field) => ({
                  ...field,
                  value: updatedReceiver.fields?.[field.id] ?? "",
                }));

                return {
                  ...receiver,
                  form: {
                    ...receiver.form,
                    ...(updatedReceiver.receiverEmail
                      ? { receiverEmail: updatedReceiver.receiverEmail }
                      : { fields: updatedFields }),
                    awaitsUpdate: false,
                    completed: receiver.formType === FormType.ACTIVE_FORM,
                  },
                };
              }
              return receiver;
            });

            return { ...item, receivers };
          });

          return {
            ...prevData,
            items: updatedItems,
          };
        });
      }

      // Filter items that still need updates
      const filteredItems = (
        updatedReceiver ? updatedItems : benefitsWithFormsToUpdate
      ).filter(
        (item) =>
          !isHiddenAutomaticBenefit(item.benefit) &&
          item.receivers?.some((receiver) => receiver.form?.awaitsUpdate)
      );

      setItemsToUpdate(filteredItems);
      setEditDataDialogOpen(true);
    },
    [benefitsWithFormsToUpdate, setDataRequiredActivities, dispatchRedux]
  );

  const renderGroupedForms = () => {
    const groupedItems =
      prepareSubscriptionGroupsSummaryForCurrentUser(itemsToUpdate);

    return groupedItems.map((group) => (
      <ItemFormToFillMUI
        key={group.id}
        item={group}
        handleToggleEditModal={onOpenDialogHandler}
      />
    ));
  };

  return (
    <>
      <ActivityItemMUI {...restProps} onClick={() => onOpenDialogHandler()}>
        {__("Uzupełnij dane")}
      </ActivityItemMUI>

      <Dialog
        open={editDataDialogOpen}
        onClose={onCloseDialogHandler}
        fullScreen={isMobile}
        title={__("Zmieniły się wymagania w formularzach")}
        sx={{
          "& .MuiDialog-paper": {
            minWidth: isMobile ? 0 : "784px",
          },
          ...(!isMobile && {
            ".MuiDialogContent-root, & .MuiDialogTitle-root": {
              p: 0,
            },
            ".MuiDialogTitle-root": {
              pb: 3,
            },
          }),
        }}
      >
        {description}
        {renderGroupedForms()}

        <Box display="flex" mt={3}>
          <Button
            variant="outlined"
            size="large"
            color="inherit"
            sx={{
              borderColor: "grey.400",
            }}
            onClick={onCloseDialogHandler}
            fullWidth={isMobile}
          >
            <ChevronLeftIcon sx={{ mr: "8px" }} />
            {__("Wróć")}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default FormFillerItem;
